import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Loader2, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import DeleteEventModal from './DeleteEventModal';
import { deleteCalendarEvent } from '../../services/api';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  start_date: string;
  start_time: string;
  end_time: string;
  description?: string;
  location?: string;
  is_all_day: boolean;
}

const MyCalendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    eventId: string;
    eventTitle: string;
  }>({
    isOpen: false,
    eventId: '',
    eventTitle: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use Node.js API instead of PHP
      const response = await fetch('/api/calendar/events');
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events || []);
      } else {
        setError(data.message || 'Failed to load calendar events');
      }
    } catch (err) {
      setError('Network error while loading calendar events');
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshEvents = async () => {
    try {
      setRefreshing(true);
      await loadEvents();
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTodayEvents = () => {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(event => event.start_date === today);
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    return events
      .filter(event => {
        const eventDate = new Date(event.start);
        return eventDate > now || (event.start_date === today && new Date(event.start) > now);
      })
      .slice(0, 10);
  };

  const getThisWeekEvents = () => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate >= now && eventDate <= weekFromNow;
    });
  };

  const isToday = (dateString: string) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  const handleDeleteClick = (eventId: string, eventTitle: string) => {
    setDeleteModal({
      isOpen: true,
      eventId,
      eventTitle
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      const success = await deleteCalendarEvent(deleteModal.eventId);
      
      if (success) {
        // Remove the event from the local state
        setEvents(prev => prev.filter(event => event.id !== deleteModal.eventId));
        setDeleteModal({ isOpen: false, eventId: '', eventTitle: '' });
      } else {
        setError('Failed to delete calendar event');
      }
    } catch (err) {
      setError('Error deleting calendar event');
      console.error('Delete error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, eventId: '', eventTitle: '' });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading calendar events...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <AlertCircle className="w-8 h-8 text-red-500 mr-3" />
          <div>
            <p className="text-red-600 font-medium">Error loading calendar</p>
            <p className="text-gray-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadEvents}
              className="mt-3 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Calendar className="w-6 h-6 text-primary-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Google Calendar</h2>
          </div>
          <button
            onClick={refreshEvents}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Total Events</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">{events.length}</p>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800 dark:text-green-200">Today</span>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">{getTodayEvents().length}</p>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-purple-800 dark:text-purple-200">This Week</span>
            </div>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 mt-1">{getThisWeekEvents().length}</p>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium text-orange-800 dark:text-orange-200">Upcoming</span>
            </div>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100 mt-1">{getUpcomingEvents().length}</p>
          </div>
        </div>
      </div>

      {/* Today's Events */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Clock className="w-5 h-5 text-green-600 mr-2" />
          Today's Schedule
        </h3>
        {getTodayEvents().length > 0 ? (
          <div className="space-y-3">
            {getTodayEvents().map((event) => (
              <div key={event.id} className="flex items-start p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <Clock className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-green-900 dark:text-green-100">{event.title}</h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {event.is_all_day ? 'All Day' : `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}
                  </p>
                  {event.description && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1 whitespace-pre-line">{event.description}</p>
                  )}
                  {event.location && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">📍 {event.location}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No events scheduled for today</p>
          </div>
        )}
      </div>

      {/* Upcoming Events */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 text-purple-600 mr-2" />
          Upcoming Events
        </h3>
        {getUpcomingEvents().length > 0 ? (
          <div className="space-y-3">
            {getUpcomingEvents().map((event) => (
              <div key={event.id} className="flex items-start p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex-shrink-0 text-center mr-4 min-w-[80px]">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(event.start_date).toLocaleDateString('en-GB', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {event.is_all_day ? 'All Day' : formatTime(event.start_time)}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">{event.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(event.start_date)}
                    {!event.is_all_day && ` at ${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}
                  </p>
                  {event.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line">{event.description}</p>
                  )}
                  {event.location && (
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">📍 {event.location}</p>
                  )}
                </div>
                {/* Delete button - only show for future events (not today) */}
                {!isToday(event.start_date) && (
                  <div className="flex-shrink-0 ml-3">
                    <button
                      onClick={() => handleDeleteClick(event.id, event.title)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                      title="Delete this event"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No upcoming events</p>
          </div>
        )}
      </div>

      {/* Delete Event Modal */}
      <DeleteEventModal
        isOpen={deleteModal.isOpen}
        eventTitle={deleteModal.eventTitle}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default MyCalendar;